import { ChevronLeft, ChevronRight, CirclePlay } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import type { DramaCategory, DramaSeries } from "~/api/drama";
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from "~/components/ui/carousel";
import { seriesGenres } from "~/lib/drama-genres";
import { cn } from "~/lib/utils";

const MAX_GENRES = 3;

/**
 * The /cinema banner carousel. Each slide carries its own dots and arrows rather than sharing one
 * set: they sit inside the text column, whose height changes with the title.
 *
 * Mobile shows the poster full-bleed and lets the text column set the height, so a two-line title
 * pushes the slide taller instead of clipping the Watch button. Desktop shows the poster whole,
 * over a blurred copy of itself.
 */
export function CinemaHero({ slides, categories }: { slides: DramaSeries[]; categories: DramaCategory[] }) {
  const [api, setApi] = useState<CarouselApi>();
  const multiple = slides.length > 1;

  return (
    <Carousel setApi={setApi} opts={{ loop: true, active: multiple }}>
      <CarouselContent className="ml-0">
        {slides.map((series, index) => {
          const genres = seriesGenres(series, categories).slice(0, MAX_GENRES);
          return (
            <CarouselItem key={series.id} className="pl-0">
              <div className="relative overflow-hidden bg-black md:h-[560px]">
                {series.thumbnail && (
                  <>
                    <img
                      src={series.thumbnail}
                      alt=""
                      className="absolute inset-0 hidden size-full scale-110 object-cover blur-2xl md:block"
                    />
                    <img
                      src={series.thumbnail}
                      alt=""
                      className="absolute top-0 left-0 aspect-[2/3] w-full object-cover md:hidden"
                    />
                  </>
                )}
                <div className="absolute inset-0 hidden bg-black/72 md:block" />
                <div className="relative mx-auto flex max-w-[1392px] flex-col pt-[250px] md:h-full md:flex-row md:items-center md:gap-[48px] md:px-[24px] md:pt-0">
                  <div className="hidden aspect-[2/3] w-[220px] shrink-0 overflow-hidden rounded-[12px] border border-white/10 bg-neutral-800 md:block lg:w-[300px]">
                    {series.thumbnail && <img src={series.thumbnail} alt="" className="size-full object-cover" />}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col items-center gap-[14px] bg-linear-to-b from-transparent to-black to-62% px-[20px] pt-[77px] pb-[26px] text-center md:items-start md:gap-[20px] md:bg-none md:p-0 md:text-left">
                    {multiple && (
                      <div className="flex items-center gap-[6px]" aria-hidden>
                        {slides.map((slide, dot) => (
                          <span
                            key={slide.id}
                            className={cn(
                              "rounded-full",
                              dot === index
                                ? "size-[7px] bg-white md:size-[8px]"
                                : "size-[6px] bg-white/30 md:size-[7px]"
                            )}
                          />
                        ))}
                      </div>
                    )}
                    <h2 className="text-[28px] leading-[1.15] font-semibold text-white md:text-[36px] md:leading-[1.1] lg:text-[44px]">
                      {series.title}
                    </h2>
                    {genres.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-[6px] md:justify-start md:gap-[8px]">
                        {genres.map((genre, i) => (
                          <span
                            key={genre}
                            className={cn(
                              "rounded-full bg-white/12 px-[10px] py-[4px] text-[12px] leading-[14px] font-medium text-white md:px-[12px] md:py-[5px] md:text-[13px] md:leading-[16px]",
                              i >= 2 && "hidden md:inline"
                            )}
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Clamped here but not on the series page: on the catalogue the synopsis is a
                        teaser, and at full length it outweighs the title and the Watch button. */}
                    {series.description && (
                      <p className="hidden max-w-[560px] text-[16px] leading-[1.55] text-neutral-300 md:line-clamp-3">
                        {series.description}
                      </p>
                    )}
                    <div className="flex items-center gap-[12px]">
                      <Link
                        id={`cinema-hero-watch-${series.id}`}
                        to={`/cinema/watch?series=${encodeURIComponent(series.id)}`}
                        className="bg-brand-500 flex h-[44px] items-center gap-[8px] rounded-full border border-black/5 px-[26px] text-[16px] leading-[20px] font-medium text-neutral-900 md:h-[48px] md:px-[28px]"
                      >
                        Watch
                        <CirclePlay className="size-[18px]" />
                      </Link>
                      {multiple && (
                        <>
                          <button
                            id={`cinema-hero-prev-${series.id}`}
                            type="button"
                            aria-label="Previous"
                            onClick={() => api?.scrollPrev()}
                            className="hidden size-[44px] cursor-pointer items-center justify-center rounded-full border border-white/40 bg-white/10 text-white md:flex"
                          >
                            <ChevronLeft className="size-[19px]" />
                          </button>
                          <button
                            id={`cinema-hero-next-${series.id}`}
                            type="button"
                            aria-label="Next"
                            onClick={() => api?.scrollNext()}
                            className="hidden size-[44px] cursor-pointer items-center justify-center rounded-full border border-white/40 bg-white/10 text-white md:flex"
                          >
                            <ChevronRight className="size-[19px]" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          );
        })}
      </CarouselContent>
    </Carousel>
  );
}
